import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { endpoints } from "@/lib/api";
import { useSettings } from "@/lib/useSettings";
import Icon from "@/components/Icon";
import ProductCard from "@/components/ProductCard";
import QuoteFormModal from "@/components/QuoteFormModal";
import { Reveal } from "@/lib/motionSafe";

export default function Offers() {
  const [items, setItems] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalProduct, setModalProduct] = useState({ id: null, name: "" });
  const { settings } = useSettings();

  useEffect(() => {
    endpoints
      .products({ on_sale: true })
      .then((data) => setItems(data))
      .catch(() => setItems([]));
  }, []);

  const openQuote = (product) => {
    setModalProduct({ id: product.id, name: product.name_ar });
    setModalOpen(true);
  };

  return (
    <div data-testid="page-offers">
      {/* Compact hero */}
      <section className="aman-hero relative pt-14 pb-16 md:pt-20 md:pb-20 overflow-hidden">
        <div className="aman-glow-orb -top-24 -end-24" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full bg-rose-500/15 border border-rose-400/40 text-rose-300 px-4 py-1.5 text-xs font-semibold">
              <Icon name="sparkles" size={14} />
              العروض والخصومات
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="font-heading mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.15]" data-testid="offers-hero-title">
              أفضل الأسعار على المنتجات المميّزة
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-5 max-w-2xl text-lg text-slate-300 leading-relaxed">
              عروض محدودة على مجموعة مختارة من كاميرات المراقبة وأنظمة الإنذار والشبكات — احجز جهازك بسعر مخفّض قبل نفاد الكمية.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Grid */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {items === null ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="offers-loading">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="animate-pulse aman-card overflow-hidden">
                  <div className="aspect-[4/3] bg-slate-100" />
                  <div className="p-5 space-y-3">
                    <div className="h-3 bg-slate-100 rounded w-1/3" />
                    <div className="h-5 bg-slate-100 rounded w-4/5" />
                    <div className="h-3 bg-slate-100 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-20 max-w-md mx-auto" data-testid="offers-empty">
              <div className="w-16 h-16 mx-auto rounded-full bg-rose-50 text-rose-500 flex items-center justify-center mb-5">
                <Icon name="sparkles" size={28} />
              </div>
              <h3 className="font-heading text-2xl font-bold text-[#0A1628]">لا توجد عروض حالياً</h3>
              <p className="mt-2 text-slate-600">تابعنا قريباً، سنُعلن عن عروض جديدة على مجموعة مختارة من المنتجات.</p>
              <Link to="/products" className="mt-6 inline-flex aman-btn-light">
                تصفح جميع المنتجات
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-8 flex items-center justify-between">
                <div className="text-slate-600 text-sm">
                  <span className="font-bold text-[#0A1628]">{items.length}</span> منتج بأسعار مخفّضة
                </div>
                <Link to="/products" className="text-[#00b8cc] hover:text-[#0A1628] text-sm font-semibold inline-flex items-center gap-1 transition-colors">
                  تصفح كل المنتجات
                  <Icon name="chevron-left" size={16} strokeWidth={2.5} />
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="offers-grid">
                {items.map((product, i) => (
                  <div key={product.id} className="flex flex-col gap-3">
                    <ProductCard product={product} delayMs={i * 60} />
                    <button
                      type="button"
                      onClick={() => openQuote(product)}
                      className="aman-btn-primary text-xs w-full justify-center py-2"
                      data-testid={`offers-quote-${product.slug}`}
                    >
                      <Icon name="send" size={14} strokeWidth={2} />
                      {product.in_stock === false ? "استفسر عن التوفر" : "اطلب عرض سعر"}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Closing CTA */}
      {items && items.length > 0 && (
        <section className="bg-[#0A1628] py-14 md:py-16 border-t border-white/5" data-testid="offers-cta">
          <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
            <Reveal>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white leading-tight">
                هل تحتاج مشورة سريعة قبل الشراء؟
              </h2>
            </Reveal>
            <Reveal delay={100}>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                {settings?.phone && (
                  <a href={`tel:${settings.phone}`} className="aman-btn-secondary text-base">
                    <Icon name="phone" size={18} />
                    اتصل بنا الآن
                  </a>
                )}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <QuoteFormModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        productId={modalProduct.id}
        productName={modalProduct.name}
      />
    </div>
  );
}
